import { NextLink, Translate, useTranslate } from '~/i18n';

import Container from '~/components/common/container';
import Image from 'next/image';

interface Link {
   label: React.ReactNode;
   href: string;
   target?: string;
   isExternal?: boolean;
}

interface PageLinksProps {
   title: React.ReactNode;
   links: Link[];
}

const Pages = [
   {
      title: <Translate id="common:pages" />,
      links: [
         { label: <Translate id="common:about us" />, href: '/about-us' },
         {
            label: <Translate id="footer:awareness platform for pilgrims and umrah performers" />,
            href: `https://learn.haj.gov.sa/landing/index-:lang.html`,
            isExternal: true,
            target: '_blank',
         },
      ],
   },
   {
      title: <Translate id="common:resources" />,
      links: [
         { label: <Translate id="footer:contact us" />, href: '/contact-us' },
         // { label: <Translate id="footer:privacy policy" />, href: '#' },
         // { label: <Translate id="footer:terms conditions" />, href: '#' },
         // { label: <Translate id="common:changelog" />, href: '#' },
         // { label: <Translate id="common:license" />, href: '#' },
      ],
   },
   {
      title: <Translate id="common:connected" />,
      links: [
         {
            label: <Translate id="common:instagram" />,
            href: 'https://www.instagram.com/visitmadinahsa',
            target: '_blank',
            isExternal: true,
         },
         {
            label: <Translate id="common:x" />,
            href: 'https://www.x.com/visitmadinahsa',
            target: '_blank',
            isExternal: true,
         },
         {
            label: <Translate id="common:youtube" />,
            href: 'https://www.youtube.com/@visitmadinahsa',
            target: '_blank',
            isExternal: true,
         },
         {
            label: <Translate id="common:facebook" />,
            href: 'https://www.facebook.com/Visitmadinahsa',
            target: '_blank',
            isExternal: true,
         },
         {
            label: <Translate id="common:snapchat" />,
            href: 'https://www.snapchat.com/add/visitmadinahsa',
            target: '_blank',
            isExternal: true,
         },
         {
            label: <Translate id="common:tiktok" />,
            href: 'https://www.tiktok.com/@visitmadinahsa',
            target: '_blank',
            isExternal: true,
         },
      ],
   },
];

const WebsiteFooter = () => {
   const d = new Date();

   return (
      <footer className="block w-full bg-black">
         <div className="hidden w-full h-24 grid-cols-6 lg:grid">
            {...Array(6)
               .fill(0)
               .map((_, idx) => (
                  <div className="relative flex w-full aspect-[4/3] h-24" key={idx}>
                     <Image
                        src={`/madinah/footer/footer-image-0${idx + 1}.png`}
                        fill
                        sizes="auto"
                        alt=""
                        priority={true}
                        quality={100}
                        className="object-cover"
                     />
                  </div>
               ))}
         </div>
         <Container>
            <div className="grid w-full grid-cols-1 lg:grid-cols-2">
               <div className="flex flex-col w-full h-full gap-4 py-6 lg:ltr:border-r lg:rtl:border-l border-r-white rtl:border-white/15 border-opacity-15 lg:py-12">
                  <Image
                     src="/madinah/footer/logo.svg"
                     alt="logo"
                     width={93}
                     height={44}
                     className="w-24 h-12"
                  />
                  <Image src="/madinah/footer/auth-logo.svg" alt="logo" width={230} height={49} />
               </div>

               <div className="flex flex-col w-full h-full gap-4 py-6 lg:py-12 lg:ltr:pl-12 lg:rtl:pr-12">
                  <div className="grid w-full grid-cols-2 gap-8 md:grid-cols-4 ">
                     {Pages.map((page, index) => (
                        <div key={index} className="w-full">
                           <PageLinks title={page.title} links={page.links} />

                           {index === 0 && (
                              <div className="mt-8">
                                 <h4 className="text-lg font-medium text-white uppercase">
                                    <Translate id="common:by email" />
                                 </h4>
                                 <div className="flex flex-col mt-1 text-gray-footer text-nowrap">
                                    <a
                                       href="mailto:Ch@amana-md.gov.sa"
                                       className="hover:text-madinah-primary">
                                       Ch@amana-md.gov.sa
                                    </a>
                                 </div>
                              </div>
                           )}

                           {/* {index === 1 && (
                              <div className="mt-8">
                                 <h4 className="text-lg font-medium text-white uppercase">
                                    <Translate id="common:by phone" />
                                 </h4>
                                 <div className="flex flex-col mt-1 text-gray-footer">
                                    <a
                                       href="tel:+966148177706"
                                       className="hover:text-madinah-primary">
                                       +(966) 14-8177706
                                    </a>
                                 </div>
                              </div>
                           )} */}
                        </div>
                     ))}
                  </div>
               </div>
            </div>
         </Container>

         <div className="py-4 bg-black border-t border-white font-secondary text-gray-footer border-opacity-15">
            <Container className="py-2 lg:py-0">
               <span className="text-xs">
                  <Translate
                     parseHTML
                     id="footer:sta copyright"
                     values={{ year: d.getFullYear() }}
                  />
               </span>
            </Container>
         </div>
      </footer>
   );
};

const PageLinks: React.FC<PageLinksProps> = ({ title, links }) => {
   const { locale } = useTranslate();
   return (
      <>
         <h4 className="text-lg font-medium text-white uppercase">{title}</h4>

         <ul className="flex flex-col mt-1 text-gray-footer">
            {links.map((link, index) => {
               const LinkComponent = link.isExternal ? 'a' : NextLink;
               return (
                  <li key={index}>
                     <LinkComponent
                        href={link.href.replace(':lang', locale)}
                        className="hover:text-madinah-primary"
                        target={link?.target}>
                        {link.label}
                     </LinkComponent>
                  </li>
               );
            })}
         </ul>
      </>
   );
};

export default WebsiteFooter;
